import { useEffect, useRef } from 'react';
import { tagAllElements, tagElement } from 'utils/dataSid';

/**
 * Dev-only 组件：自动为 DOM 中有背景色的元素和 Ant Design 组件打上 data-sid 标记。
 *
 * - 页面加载时全量标记
 * - 监听 DOM 变化，为新出现的元素（Modal、Dropdown、动态消息等）自动标记
 * - 仅在 import.meta.env.DEV 生效
 */
export const DataSidTagger = () => {
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // 首屏标记
    const initialCount = tagAllElements();
    if (import.meta.env.DEV) {
      console.log(`[data-sid] initial tagged: ${initialCount} elements`);
    }

    // MutationObserver 处理动态内容
    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // 先标记新增节点自身（TreeWalker.nextNode 从子节点开始，会跳过根）
              if (!node.hasAttribute('data-sid')) {
                tagElement(node);
              }
              // 再遍历子树
              const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT);
              let el: Element | null = walker.currentNode as Element;
              while ((el = walker.nextNode() as Element | null)) {
                if (el.hasAttribute('data-sid')) continue;
                const rect = el.getBoundingClientRect();
                if (rect.width < 10 && rect.height < 10) continue;
                tagElement(el);
              }
            }
          }
        }
      }
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  if (!import.meta.env.DEV) return null;
  return null;
};

export default DataSidTagger;
